<script setup>
import { computed } from 'vue';
import { Radar } from 'vue-chartjs';
import {
  Chart as ChartJS, Title, Tooltip, Legend,
  RadialLinearScale, PointElement, LineElement, Filler
} from 'chart.js';

ChartJS.register(Title, Tooltip, Legend, RadialLinearScale, PointElement, LineElement, Filler);

// scoreLabels 플러그인을 이 컴포넌트에서 완전히 제거
if (ChartJS.registry.plugins.get('scoreLabels')) {
  ChartJS.unregister(ChartJS.registry.plugins.get('scoreLabels'));
}

const props = defineProps({
  chartType: { 
    type: String,
    required: true,
    validator: (value) => ['literacy', 'numeracy'].includes(value)
  },
  itemLabels: { 
    type: Array,
    required: true
  },
  ourClassPercentages: { 
    type: Array,
    default: () => []
  },
  overallPercentages: { 
    type: Array,
    default: () => []
  },
  chartHeight: { 
    type: String,
    default: '350px' 
  }
});

const chartData = computed(() => {
  const ourClassGraphColor = '#00A496';  
  const overallGraphColorLiteracy = '#4362CC';
  const overallGraphColorNumeracy = '#CE4E56';

  const ourClassBorderColor = ourClassGraphColor;  
  const overallBorderColor = props.chartType === 'literacy' ? overallGraphColorLiteracy : overallGraphColorNumeracy;

  const originalLabels = props.itemLabels;
  const originalOurData = props.ourClassPercentages;
  const originalOverallData = props.overallPercentages;

  const labelsLength = originalLabels.length;

  const reorderedLabels = [];
  const reorderedOurData = [];
  const reorderedOverallData = [];
  
  const visitedIndices = new Set(); // 이미 처리된 인덱스를 추적

  // 순서 변경
  const specificOrder = [0, 2, 3, 1]; 

  specificOrder.forEach(idx => {
    if (idx < labelsLength && !visitedIndices.has(idx)) { // 인덱스가 유효하고 아직 처리되지 않았다면
      reorderedLabels.push(originalLabels[idx]);
      reorderedOurData.push(originalOurData[idx]);
      reorderedOverallData.push(originalOverallData[idx]);
      visitedIndices.add(idx); // 처리된 인덱스 추가
    }
  });

  // 나머지 항목들을 기존 순서대로 추가 (시계방향 유지)
  for (let i = 0; i < labelsLength; i++) {
    if (!visitedIndices.has(i)) { 
      reorderedLabels.push(originalLabels[i]);
      reorderedOurData.push(originalOurData[i]);
      reorderedOverallData.push(originalOverallData[i]);
      visitedIndices.add(i);
    }
  }

  return {
    labels: reorderedLabels, // 재정렬된 라벨 사용
    datasets: [
      {
        label: '우리',
        data: reorderedOurData, 
        borderColor: ourClassBorderColor, 
        borderWidth: 2,
        pointBackgroundColor: ourClassBorderColor, 
        pointBorderColor: ourClassBorderColor,    
        pointRadius: 3,                   
        fill: false,
      },
      {
        label: '전체',
        data: reorderedOverallData, 
        backgroundColor: `${overallBorderColor}10`,
        borderColor: overallBorderColor,
        borderWidth: 2,
        pointBackgroundColor: overallBorderColor,
        pointBorderColor: overallBorderColor, 
        pointRadius: 3,   
        fill: 'start',
      },
    ],
  };

});

const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false, 
  layout: { // 캔버스 패딩
    padding: {
      left: 8, 
      right: 8, 
      top: 5, 
      bottom: 5 
    }
  },
  scales: {
    r: {
      angleLines: {
        color: '#ccc', 
        display: true, // 각도선은 유지 (항목 개수에 따라 그려짐)
      },
      grid: {
        color: '#ccc', // 회색 그리드 선 유지
        display: true, // 그리드 선 자체는 표시
        lineWidth: 2,     
      },
      pointLabels: {
        display: false, // 축 라벨은 숨김
      },
      min: -25,
      max: 100, // 최대 값 100%
      ticks: { 
        display: false, // 퍼센트 수치 숨김     
        stepSize: 25, 
      },      
    },
  },
  plugins: {
    legend: {
      display: false, // 범례 숨김
    },
    tooltip: {
      display: false, // 툴팁 숨김
      enabled: false
    },
    scoreLabels: false, // GrowthChart의 scoreLabels 플러그인 완전 비활성화
    // 추가적인 텍스트 렌더링 차단
    afterDatasetsDraw: function() {
      // 아무것도 그리지 않음으로써 플러그인 무력화
      return false;
    }
  },
}));
</script>

<template>    
  <div :style="{height: chartHeight, width:chartHeight}" class="chart" :class="props.chartType">
      <ul>
        <li>100%</li>
        <li>75%</li>
        <li>50%</li>
        <li>25%</li>
        <li>0%</li>
      </ul>
      <Radar :data="chartData" :options="chartOptions"></Radar>
  </div>     
</template>

<style lang="scss" scoped>
.chart {
  position: relative;
  z-index: 1;
  ul {
    position: absolute;
    top: 4px;
    right: -55px;
    display: flex;
    flex-direction: column;
    gap: 22px;
    line-height: .8;
    font-size: 14px;
    color: #666;
    text-align: right;
    z-index: -1;
    li {
      position: relative;
      &::before {
        content: '';
        position: absolute;
        left: -195px;
        bottom: 50%;
        transform: translateY(-50%);
        width: 185px;
        border-top: 2px dashed #E9E9E9;     
      }
    }
  }
  &::after {
    content: "";   
    width: 45px;
    height: 45px;
    position: absolute;
    z-index: 1;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) rotate(45deg);
  }
  &.literacy::after {
    background-color: #f3f5fc;     
  }  
  &.numeracy::after {
    background-color: #fcf4f4;     
  }  
}
</style>